// src/admin/components/AdminLayout.jsx
// Shared chrome for every authenticated admin page: top nav (Merchants,
// Coupons, ...) plus "Signed in as {email}" and Log out, matching the
// pattern that used to live only inside AdminTickets.jsx. Wrap page
// content in <AdminLayout>...</AdminLayout> inside the RequireAdmin gate.
//
// Calls useAdmin() itself (one GET /admin/me per page load) so every
// page gets identity + logout for free without duplicating the fetch
// call site-by-site -- same call AdminTickets.jsx already made on its
// own, just centralized now.

import { NavLink, useNavigate } from "react-router-dom";
import { useAdmin } from "@/admin/hooks/useAdmin";
import { clearAdminSession } from "@/admin/lib/adminAuth";

const NAV_LINKS = [
  { to: "/admin/merchants", label: "Merchants" },
  { to: "/admin/coupons", label: "Coupons" },
  { to: "/admin/referrals", label: "Referrals" },
  { to: "/admin/feedback", label: "Feedback" },
  { to: "/admin/unblock", label: "Unblock" },
];

export default function AdminLayout({ children }) {
  const { admin } = useAdmin();
  const navigate = useNavigate();

  function handleLogout() {
    clearAdminSession();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-ink/10 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-4 md:px-8">
          <div className="flex items-center gap-6">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-hotpink">
              CheckoutDM Admin
            </p>
            <nav className="flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                      isActive ? "bg-hotpink/10 text-hotpink" : "text-ink/60 hover:text-ink"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 text-sm text-ink/60">
            {admin && (
              <span>
                Signed in as <span className="font-semibold text-ink">{admin.email}</span>
              </span>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-xl border border-ink/15 px-3 py-1.5 text-sm font-semibold transition-colors hover:bg-cream"
            >
              Log out
            </button>
          </div>
        </div>
      </header>
      <main className="p-6 md:p-8">{children}</main>
    </div>
  );
}
